import type { MenuScreen, MenuContext, TableMode } from '../types.js';
import { TABLE_MODES, settingsForTableMode, tableModeFromSettings } from '../types.js';
import { createScreenShell, createMenuHeader, createCrestButton, createCopyButton, createCycleBadge, createInviteFriendButton, el, withFocusPreserved } from '@bn-games/ui-shell';

function shareLinkFor(code: string): string {
  return `${window.location.origin}${window.location.pathname}#room=${code}`;
}
import { t } from '../../i18n/I18n.js';
import { parseVariant } from '../../network/protocol.js';
import { seatsFor } from '../../game/types.js';
import type { PlayerCount } from '../../game/variants/types.js';
import { ROOM_CODE_LENGTH } from '@bn-games/network-protocol';

function formatRoomCode(code: string | null): string {
  if (!code) return '·'.repeat(ROOM_CODE_LENGTH);
  return code.split('').join(' ');
}

function tableModeLabel(mode: TableMode): string {
  switch (mode) {
    case '2p': return t('playerCount.two');
    case '3p': return t('playerCount.three');
    case '4p': return t('playerCount.four');
    case '4p-2v2': return t('playerCount.fourPartners');
  }
}

/**
 * N-seat picker styled with the shared `.side-picker` shell. Each swatch is a
 * numbered chip showing seat position name (south/west/north/east) so the host
 * sees both the engine seat index and the table position the server will rotate
 * to. Re-implemented locally (rather than extending `SidePicker` to N) because
 * `SidePicker` is still the binary white/black picker for checkers/backgammon
 * and the deferred generalization isn't on this milestone's path.
 */
function createSeatPicker(opts: {
  readonly label: string;
  readonly playerCount: PlayerCount;
  readonly value: number;
  readonly onChange: (seat: number) => void;
}): HTMLElement {
  const seats = seatsFor(opts.playerCount);
  const swatches = seats.map((seat, i) => {
    const classes = ['side-picker__swatch'];
    if (i === opts.value) classes.push('side-picker__swatch--selected');
    return el(
      'button',
      {
        class: classes.join(' '),
        attrs: {
          type: 'button',
          'aria-label': t(`seats.${seat}`),
          // Matches the SidePicker pattern so withFocusPreserved() can restore
          // focus to this specific swatch after the screen rebuild.
          'data-side-value': String(i),
        },
        on: { click: () => opts.onChange(i) },
      },
      [
        el('span', { class: 'side-picker__caption', text: t(`seats.${seat}`) }),
      ],
    );
  });
  return el(
    'div',
    {
      class: 'side-picker',
      attrs: { 'data-focus-key': `side:${opts.label}` },
    },
    [
      el('div', { class: 'side-picker__label', text: opts.label }),
      el('div', { class: 'side-picker__row' }, swatches),
    ],
  );
}

/**
 * Two phases on the same screen:
 * 1. Pre-create — host picks table mode, seat, and clicks Create to open the
 *    room with `seatCount` + `preferredSeat` in CREATE_ROOM.
 * 2. Post-create — show the room code + waiting status until the lobby fills.
 */
export function createOnlineCreateScreen(): MenuScreen {
  let frame: HTMLElement | null = null;
  let root: HTMLElement | null = null;
  let unsubRoom: (() => void) | null = null;
  let created = false;

  function paint(ctx: MenuContext): void {
    if (!root) return;
    while (root.firstChild) root.removeChild(root.firstChild);
    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;

    body.appendChild(createMenuHeader({ title: t('online.create.title') }));

    if (!created) {
      const settings = ctx.getSettings();
      const mode = tableModeFromSettings(settings);
      const seatCount = ctx.getPendingOnlineSeatCount();
      const chosenSeat = ctx.getPendingOnlineSeat();

      const tableBadge = createCycleBadge<TableMode>({
        label: t('online.create.seatCountLabel'),
        value: mode,
        options: TABLE_MODES,
        format: tableModeLabel,
        onChange: (next) => {
          const coerced = settingsForTableMode(next, settings.variant);
          ctx.updateSettings(coerced);
          ctx.setPendingOnlineSeatCount(coerced.playerCount as PlayerCount);
          ctx.setPendingOnlineSeat(0);
          withFocusPreserved(() => paint(ctx), root ?? undefined);
        },
      });

      const seatPicker = createSeatPicker({
        label: t('online.create.seatLabel'),
        playerCount: seatCount,
        value: Math.min(chosenSeat, seatCount - 1),
        onChange: (next) => {
          ctx.setPendingOnlineSeat(next);
          withFocusPreserved(() => paint(ctx), root ?? undefined);
        },
      });

      const createBtn = createCrestButton({
        label: t('online.create.create'),
        primary: true,
        onClick: () => {
          created = true;
          ctx.setOpponent('online');
          ctx.createRoom();
          paint(ctx);
        },
      });

      const center = el('div', { class: 'about-center' }, [
        tableBadge,
        seatPicker,
        el('div', { class: 'button-stack' }, [createBtn]),
      ]);

      const back = el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({
          label: t('common.back'),
          small: true,
          onClick: () => ctx.navigate('play'),
        }),
      ]);

      body.appendChild(center);
      body.appendChild(back);
      root.appendChild(frame);
      return;
    }

    const info = ctx.roomClient.getInfo();
    const center = el('div', { class: 'online-center' });
    const label = el('div', { class: 'online-label', text: t('online.create.codeLabel') });
    const codeNode = el('div', { class: 'online-code', text: formatRoomCode(info.roomCode) });
    const share = el('div', { class: 'online-hint', text: t('online.create.share') });
    const variant = parseVariant(info.variant ?? undefined);
    const variantNode = el('div', {
      class: 'online-hint',
      text: t('online.create.variantLabel', { variant: t(`settings.variants.${variant}`) }),
    });
    // Surface the host's verifiable-shuffle preference while they wait so they
    // and the joiner share a visible reference point for what the match will
    // be running. The joiner sees the same flag echoed via ROOM_JOINED.
    const verifiableNode = info.verifiable
      ? el('div', {
          class: 'online-hint',
          text: `${t('online.verifiable.hostBadge')} — ${t('online.verifiable.hostHint')}`,
        })
      : null;
    const statusNode = el('div', { class: 'online-status waiting-dots' });

    switch (info.state) {
      case 'connecting':
        statusNode.textContent = t('online.status.connecting');
        statusNode.classList.add('waiting-dots');
        break;
      case 'creating':
        statusNode.textContent = t('online.status.creating');
        statusNode.classList.add('waiting-dots');
        break;
      case 'waiting_for_opponent':
        statusNode.textContent = t('online.status.waitingForOpponent');
        statusNode.classList.add('waiting-dots');
        break;
      case 'ready':
        statusNode.textContent = t('online.status.opponentJoined');
        statusNode.classList.remove('waiting-dots');
        break;
      case 'error':
        statusNode.textContent = info.errorMessage ?? t('online.status.error');
        statusNode.classList.remove('waiting-dots');
        break;
      default:
        statusNode.textContent = info.state;
        statusNode.classList.remove('waiting-dots');
    }

    const roomCode = info.roomCode;
    const shareRow = roomCode
      ? el('div', { class: 'button-row' }, [
          createCopyButton({
            label: t('online.share.copyCode'),
            copiedLabel: t('online.share.copied'),
            getText: () => roomCode,
          }),
          createCopyButton({
            label: t('online.share.copyLink'),
            copiedLabel: t('online.share.copied'),
            getText: () => shareLinkFor(roomCode),
          }),
        ])
      : null;

    const inviteBtn = roomCode ? createInviteFriendButton({ game: 'domino', roomCode }) : null;
    const inviteRow = inviteBtn ? el('div', { class: 'button-row' }, [inviteBtn]) : null;

    center.appendChild(label);
    center.appendChild(codeNode);
    if (shareRow) center.appendChild(shareRow);
    if (inviteRow) center.appendChild(inviteRow);
    center.appendChild(share);
    center.appendChild(variantNode);
    if (verifiableNode) center.appendChild(verifiableNode);
    center.appendChild(statusNode);

    const back = el('div', { class: 'button-row back-button-row' }, [
      createCrestButton({
        label: t('common.back'),
        small: true,
        onClick: () => {
          ctx.cancelRoom();
          ctx.navigate('play');
        },
      }),
    ]);

    body.appendChild(center);
    body.appendChild(back);
    root.appendChild(frame);
  }

  return {
    mount(rootEl, ctx) {
      root = rootEl;
      created = false;
      unsubRoom = ctx.roomClient.onUpdate(() => paint(ctx));
      paint(ctx);
    },
    update(ctx) {
      paint(ctx);
    },
    unmount() {
      unsubRoom?.();
      unsubRoom = null;
      frame?.remove();
      frame = null;
      root = null;
      created = false;
    },
  };
}
